/**
 * Kachelweiser Bildvergleich und Klassifikation.
 *
 * Übernimmt den Algorithmus aus old/electron-av/src/renderer/av/TileImage.js
 * und old/av-coding/.../util2/Tile.java, aber mit zwei Änderungen:
 *
 *  - Der Vergleich läuft über einen Uint32Array-View auf die ImageData:
 *    ein Vergleich pro Pixel statt drei Byte-Vergleiche.
 *  - Die Klassifikation in "wenige Farben" (Text/UI) und "viele Farben"
 *    (Foto/Video) ist aktiv. Im Original war sie zwar vorbereitet
 *    (Tile.countGreater), der Umschaltcode aber auskommentiert.
 */

import { CLASS_LOSSLESS, CLASS_LOSSY, COLOR_THRESHOLD, TILE_DIM, countTilesX, countTilesY } from '../../shared/constants.ts';

export interface DiffResult {
  /** Indizes geänderter Kacheln mit vielen Farben. */
  lossy: number[];
  /** Indizes geänderter Kacheln mit wenigen Farben. */
  lossless: number[];
}

export type ClassifyMode = 'mixed' | 'lossy' | 'lossless';

/**
 * Kapazität der Farbtabelle in classify(): die nächste Zweierpotenz über dem
 * Dreifachen der Schwelle. Aus der Schwelle abgeleitet, damit die Tabelle nicht
 * zu klein wird, wenn jemand COLOR_THRESHOLD anhebt – bei voller Tabelle liefe
 * die Sondierung endlos. Der Lastfaktor bleibt so unter 0,35.
 */
const COLOR_SLOTS = 1 << Math.ceil(Math.log2((COLOR_THRESHOLD + 2) * 3));
const COLOR_MASK = COLOR_SLOTS - 1;

export class Tiler {
  private previous: Uint32Array | null = null;
  /** Ob `previous` einen gültigen Stand enthält – der Puffer selbst bleibt erhalten. */
  private hasPrevious = false;
  readonly tilesX: number;
  readonly tilesY: number;

  /**
   * Farbtabelle für die Klassifikation: offene Adressierung mit linearer
   * Sondierung. Statt sie je Kachel zu leeren, zählt `colorGeneration` hoch –
   * Einträge mit älterem Stempel gelten als leer.
   */
  private readonly colorKeys = new Int32Array(COLOR_SLOTS);
  private readonly colorStamps = new Int32Array(COLOR_SLOTS);
  private colorGeneration = 0;

  constructor(
    readonly width: number,
    readonly height: number,
  ) {
    this.tilesX = countTilesX(width);
    this.tilesY = countTilesY(height);
  }

  get tileCount(): number {
    return this.tilesX * this.tilesY;
  }

  /** Verwirft die Vergleichsbasis, sodass das nächste Bild als vollständig geändert gilt. */
  invalidate(): void {
    // Nur den Stand verwerfen, nicht den Puffer – der wird gleich wieder gefüllt.
    this.hasPrevious = false;
  }

  /**
   * Vergleicht das übergebene Bild mit dem zuletzt übernommenen.
   *
   * Die Vergleichsbasis wird hier NICHT fortgeschrieben – das geschieht erst
   * über `commit()`, wenn das Frame tatsächlich verschickt wurde. Sonst gingen
   * Änderungen verloren, wenn ein Frame wegen voller Leitung verworfen wird.
   */
  diff(data: ImageData, mode: ClassifyMode = 'mixed'): DiffResult {
    const pixels = new Uint32Array(data.data.buffer);
    const previous = this.hasPrevious ? this.previous : null;
    const result: DiffResult = { lossy: [], lossless: [] };

    for (let ty = 0; ty < this.tilesY; ty++) {
      for (let tx = 0; tx < this.tilesX; tx++) {
        if (previous && !this.tileChanged(pixels, previous, tx, ty)) continue;

        const index = ty * this.tilesX + tx;
        const cls = this.classify(pixels, tx, ty, mode);
        (cls === CLASS_LOSSLESS ? result.lossless : result.lossy).push(index);
      }
    }
    return result;
  }

  /**
   * Übernimmt das Bild als neue Vergleichsbasis.
   *
   * Der Puffer wird einmal angelegt und danach überschrieben: Ein `slice()` je
   * Frame hieße bei 4K 33 MB Allokation pro Bild, mitsamt dem Aufräumen danach.
   */
  commit(data: ImageData): void {
    const pixels = new Uint32Array(data.data.buffer);
    if (!this.previous || this.previous.length !== pixels.length) {
      this.previous = new Uint32Array(pixels.length);
    }
    this.previous.set(pixels);
    this.hasPrevious = true;
  }

  private tileChanged(pixels: Uint32Array, previous: Uint32Array, tx: number, ty: number): boolean {
    const x0 = tx * TILE_DIM;
    const y0 = ty * TILE_DIM;
    const x1 = Math.min(x0 + TILE_DIM, this.width);
    const y1 = Math.min(y0 + TILE_DIM, this.height);

    for (let y = y0; y < y1; y++) {
      const row = y * this.width;
      for (let x = x0; x < x1; x++) {
        if (pixels[row + x] !== previous[row + x]) return true;
      }
    }
    return false;
  }

  /**
   * Zählt die unterschiedlichen Farbwerte einer Kachel und bricht ab, sobald
   * die Schwelle überschritten ist – wie Tile.countGreater im Original.
   *
   * Statt eines `Set` je Kachel läuft das über eine wiederverwendete Tabelle:
   * Bei tausenden geänderten Kacheln pro Frame kostet das Anlegen und Füllen
   * eines Sets samt Boxing mehr als der Pixelvergleich selbst.
   */
  private classify(pixels: Uint32Array, tx: number, ty: number, mode: ClassifyMode): number {
    if (mode === 'lossy') return CLASS_LOSSY;
    if (mode === 'lossless') return CLASS_LOSSLESS;

    const x0 = tx * TILE_DIM;
    const y0 = ty * TILE_DIM;
    const x1 = Math.min(x0 + TILE_DIM, this.width);
    const y1 = Math.min(y0 + TILE_DIM, this.height);

    const generation = this.nextColorGeneration();
    const { colorKeys, colorStamps } = this;
    let distinct = 0;

    for (let y = y0; y < y1; y++) {
      const row = y * this.width;
      for (let x = x0; x < x1; x++) {
        // Als Int32 ablegen: Uint32Array liefert Werte über 2^31, die in der
        // Tabelle sonst nicht identisch zurückkämen.
        const color = pixels[row + x]! | 0;

        let slot = (Math.imul(color, 0x9e3779b1) >>> 0) & COLOR_MASK;
        while (colorStamps[slot] === generation && colorKeys[slot] !== color) {
          slot = (slot + 1) & COLOR_MASK;
        }
        if (colorStamps[slot] === generation) continue; // Farbe schon gezählt.

        colorStamps[slot] = generation;
        colorKeys[slot] = color;
        if (++distinct > COLOR_THRESHOLD) return CLASS_LOSSY;
      }
    }
    return CLASS_LOSSLESS;
  }

  /** Nächster Stempel; beim Überlauf wird die Tabelle einmal echt geleert. */
  private nextColorGeneration(): number {
    if (this.colorGeneration === 0x7fffffff) {
      this.colorStamps.fill(0);
      this.colorGeneration = 0;
    }
    return ++this.colorGeneration;
  }
}
